/**
 * Charm personalization: cart-add sequencing and cart-render glue.
 *
 * Plain ES module, not a custom element (AD-7) — it only listens on
 * `document` / `window` and owns no visible DOM of its own. Kept separate
 * from `personalize-charms.js` so UI bugs cannot affect cart-network
 * correctness (AD-7).
 *
 * Story 3.1: liveness marker, pending-set maps, empty `_charms_bundle_id`
 * injection, window-capture submit guard (UUID + inFlight duplicate kill).
 *
 * Story 3.2: after successful native Host CartAddEvent, one keepalive JSON
 * `/cart/add.js` for charms nested via `parent_line_key` (Host lookup:
 * variant + `_charms_bundle_id`) with `_charms_set_id` / `_charms_position`
 * only — never `_charms_bundle_id` on charm lines — then CartUpdateEvent
 * + FR-24 clear.
 *
 * Story 3.3: on composite failure — rollback Host via `/cart/change.js`
 * quantity 0 (match variant + `_charms_bundle_id`) + FR-22 named error;
 * also clean orphan Host on native max-qty `didError`.
 *
 * Story 3.6: Host qty >0→0 — Shopify native nested cascade is primary
 * (AD-5). Theme JS `/cart/update.js` is fallback only if children of that
 * Host key remain. Match orphans by `parent_relationship.parent_key`,
 * never `_charms_bundle_id` on charm lines. Qty stay >0 never touches
 * charms (FR-27).
 *
 * Story 3.7: cart-level "Remove Set" zeros one `_charms_set_id` via
 * `/cart/update.js` (AD-5b / FR-29); Host and sibling sets untouched.
 *
 * Submit guard listens on `window` (capture), not `document`: Horizon's
 * `on:submit` is a document capture listener registered earlier; a document
 * sibling would run *after* FormData is built and Host would get an empty
 * `_charms_bundle_id`. Window capture runs before document capture.
 *
 * @see architecture.md §6
 * @see ARCHITECTURE-SPINE.md AD-2, AD-2b, AD-3, AD-5, AD-5b, AD-7
 */

import { ThemeEvents, CartUpdateEvent } from '@theme/events';

document.documentElement.dataset.personalizeCartReady = 'true';

/**
 * @typedef {object} PersonalizeCartCharm
 * @property {string} symbol
 * @property {string} color
 * @property {string} variantId
 */

/**
 * @typedef {object} PersonalizeCartSet
 * @property {string} setId
 * @property {PersonalizeCartCharm[]} charms
 */

/**
 * @typedef {object} BundleState
 * @property {string | null} bundleId
 * @property {boolean} inFlight
 * @property {PersonalizeCartSet[]} [flightSets] snapshot at submit — freeze for composite
 */

/** @type {Map<string, PersonalizeCartSet[]>} keyed by hostProductId — AD-7 */
const pendingSetsByHost = new Map();

/** @type {Map<string, BundleState>} keyed by hostProductId */
const bundleStateByHost = new Map();

const BUNDLE_INPUT_NAME = 'properties[_charms_bundle_id]';

/** Hang fallback — clear inFlight if native+composite never completes. */
const IN_FLIGHT_SAFETY_MS = 8000;

/** Match Horizon product-form error display duration. */
const PARTIAL_FAILURE_ERROR_DISPLAY_MS = 10000;

/** @type {Map<string, ReturnType<typeof setTimeout>>} */
const inFlightSafetyTimers = new Map();

/** @type {Map<string, ReturnType<typeof setTimeout>>} */
const partialFailureErrorTimers = new Map();

/** @type {ReturnType<typeof setTimeout> | 0} */
let cartBubbleSyncTimer = 0;

/**
 * Snapshot for Host >0→0 detection (AD-5). Primed on load; refreshed on cart mutations.
 * @type {{ items?: Array<{ key?: string, properties?: Record<string, string | null | undefined> }> } | null}
 */
let lastKnownCart = null;

/** Prevents cascade remorph / personalize-cart events from re-entering AD-5. */
let cascadeInFlight = false;

/** Prevents double-click on Remove Set while a set-scoped update is in flight. */
let removeSetInFlight = false;

/**
 * Host = `_charms_bundle_id` present AND `_charms_set_id` / `_charms_position` absent
 * (same rule as `rollbackHostLine`).
 * @param {{ properties?: Record<string, string | null | undefined> } | null | undefined} item
 * @returns {boolean}
 */
function isCharmsHostLine(item) {
  const props = item?.properties ?? {};
  if (props._charms_bundle_id == null || props._charms_bundle_id === '') return false;
  if (props._charms_set_id != null || props._charms_position != null) return false;
  return true;
}

/**
 * Find a charms Host present in `prev` but fully gone in `next` (no Host for that
 * `_charms_bundle_id`). Match by bundle id — Shopify may rotate the line `key` on
 * quantity changes, so key absence alone is NOT Host removal (FR-27).
 * @param {{ items?: Array<{ key?: string, properties?: Record<string, string | null | undefined> }> } | null} prev
 * @param {{ items?: Array<{ key?: string, properties?: Record<string, string | null | undefined> }> } | null} next
 * @returns {{ key?: string, properties?: Record<string, string | null | undefined> } | null}
 */
function findHostLineThatWentToZero(prev, next) {
  const prevItems = Array.isArray(prev?.items) ? prev.items : [];
  const nextItems = Array.isArray(next?.items) ? next.items : [];
  if (prevItems.length === 0) return null;

  const nextHostBundleIds = new Set();
  for (const item of nextItems) {
    if (!isCharmsHostLine(item)) continue;
    const bundleId = item?.properties?._charms_bundle_id;
    if (typeof bundleId === 'string' && bundleId) nextHostBundleIds.add(bundleId);
  }

  for (const item of prevItems) {
    if (!isCharmsHostLine(item)) continue;
    const bundleId = item?.properties?._charms_bundle_id;
    if (typeof bundleId !== 'string' || !bundleId) continue;
    if (!nextHostBundleIds.has(bundleId)) return item;
  }
  return null;
}

/**
 * Nested parent line key from Ajax (`parent_key`) or Liquid-shaped (`parent.key`) cart items.
 * @param {{ parent_relationship?: { parent_key?: string, parent?: { key?: string } } } | null | undefined} item
 * @returns {string}
 */
function nestedParentKey(item) {
  const rel = item?.parent_relationship;
  if (!rel || typeof rel !== 'object') return '';
  if (typeof rel.parent_key === 'string' && rel.parent_key) return rel.parent_key;
  const parentKey = rel.parent?.key;
  if (typeof parentKey === 'string' && parentKey) return parentKey;
  return '';
}

/**
 * Fallback orphan keys: nested children of the removed Host still in `next`.
 * Native cascade already cleared them → []. Never keys off charm `_charms_bundle_id`.
 * @param {{ items?: Array<{ key?: string, parent_relationship?: { parent_key?: string, parent?: { key?: string } } }> } | null} prev
 * @param {{ items?: Array<{ key?: string, parent_relationship?: { parent_key?: string, parent?: { key?: string } } }> } | null} next
 * @param {{ key?: string } | null | undefined} removedHost
 * @returns {string[]}
 */
function collectOrphanKeys(prev, next, removedHost) {
  const removedKey = removedHost?.key;
  if (typeof removedKey !== 'string' || removedKey === '') return [];

  const nextItems = Array.isArray(next?.items) ? next.items : [];
  const nextKeySet = new Set(
    nextItems.map((item) => item?.key).filter((key) => typeof key === 'string' && key.length > 0)
  );
  // Parent still in cart → nested children are not orphans (FR-27 / Host present).
  if (nextKeySet.has(removedKey)) return [];

  const fromNext = nextItems
    .filter((item) => nestedParentKey(item) === removedKey)
    .map((item) => item?.key)
    .filter((key) => typeof key === 'string' && key.length > 0);

  const prevItems = Array.isArray(prev?.items) ? prev.items : [];
  const fromPrevStillPresent = prevItems
    .filter((item) => nestedParentKey(item) === removedKey)
    .map((item) => item?.key)
    .filter((key) => nextKeySet.has(key));

  return [...new Set([...fromNext, ...fromPrevStillPresent])];
}

/**
 * Best-effort prime so the first Host remove can be detected.
 * @returns {Promise<void>}
 */
async function primeLastKnownCart() {
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    lastKnownCart = await res.json();
  } catch {
    // null-safe — cascade no-ops until a later successful snapshot
  }
}

/**
 * Refresh snapshot from `/cart.js` (best-effort).
 * @returns {Promise<object | null>}
 */
async function refreshLastKnownCartFromServer() {
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return lastKnownCart;
    lastKnownCart = await res.json();
    return lastKnownCart;
  } catch {
    return lastKnownCart;
  }
}

/**
 * Zero cart lines by key via one `/cart/update.js` + section remorph.
 * Shared by AD-5 Host cascade and AD-5b Remove Set.
 * @param {string[]} lineKeys
 * @returns {Promise<void>}
 */
async function zeroCartLinesByKeys(lineKeys) {
  const keys = Array.isArray(lineKeys)
    ? lineKeys.filter((key) => typeof key === 'string' && key.length > 0)
    : [];
  if (keys.length === 0) return;

  cascadeInFlight = true;
  try {
    const sections = collectCartSectionIds();
    /** @type {{ updates: Record<string, number>, sections?: string, sections_url?: string }} */
    const body = {
      updates: Object.fromEntries(keys.map((key) => [key, 0])),
    };
    if (sections) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }

    const updateRes = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!updateRes.ok) {
      throw new Error(`Cart update failed (${updateRes.status})`);
    }
    const updateData = await updateRes.json();
    if (updateData?.errors) {
      throw new Error(String(updateData.errors));
    }

    await refreshCartUiAfterMutation(updateData?.sections);
    await refreshLastKnownCartFromServer();
  } catch (err) {
    console.error('[personalize-cart] zeroCartLinesByKeys failed', err);
    // Keep snapshot honest even on failure so a retry can still detect state.
    await refreshLastKnownCartFromServer();
    throw err;
  } finally {
    cascadeInFlight = false;
  }
}

/**
 * Collect Charm line keys for one `_charms_set_id` (never Host / other sets).
 * @param {{ items?: Array<{ key?: string, properties?: Record<string, string | null | undefined> }> } | null | undefined} cart
 * @param {string} setId
 * @returns {string[]}
 */
function collectSetLineKeys(cart, setId) {
  if (typeof setId !== 'string' || setId === '') return [];
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return items
    .filter((item) => {
      const props = item?.properties ?? {};
      return props._charms_set_id === setId;
    })
    .map((item) => item?.key)
    .filter((key) => typeof key === 'string' && key.length > 0);
}

/**
 * Fallback only: zero nested children of the removed Host that Shopify left behind.
 * Native cascade already cleared them → no-op (no `/cart/update.js`).
 * @param {{ key?: string } | null | undefined} removedHost
 * @param {{ items?: Array<{ key?: string, parent_relationship?: { parent_key?: string, parent?: { key?: string } } }> }} cart
 * @returns {Promise<void>}
 */
async function cascadeOrphanCharmsForRemovedHost(removedHost, cart) {
  const orphanKeys = collectOrphanKeys(lastKnownCart, cart, removedHost);
  if (orphanKeys.length === 0) {
    lastKnownCart = cart;
    return;
  }

  try {
    await zeroCartLinesByKeys(orphanKeys);
  } catch (err) {
    console.error('[personalize-cart] Host cascade fallback failed', err);
  }
}

/**
 * @param {string} hostProductId
 * @returns {HTMLElement | null}
 */
function findProductFormComponent(hostProductId) {
  if (!hostProductId) return null;
  return document.querySelector(
    `product-form-component[data-product-id="${CSS.escape(hostProductId)}"]`
  );
}

/**
 * @param {HTMLElement} productFormComponent
 * @returns {HTMLFormElement | null}
 */
function findProductForm(productFormComponent) {
  return (
    productFormComponent.querySelector('form[data-type="add-to-cart-form"]') ??
    productFormComponent.querySelector('form')
  );
}

/**
 * Horizon `handleSubmit` ignores `defaultPrevented` and only early-returns when
 * an ATC button is `.disabled`. Use disable ONLY on duplicate in-flight submits —
 * never for the whole Host+composite window (felt stuck ~15s).
 * @param {HTMLElement} productFormComponent
 */
function disableFormSubmitControls(productFormComponent) {
  productFormComponent.querySelectorAll('add-to-cart-component').forEach((el) => {
    const component = /** @type {{ disable?: () => void }} */ (el);
    if (typeof component.disable === 'function') {
      component.disable();
      return;
    }
    el.querySelectorAll('button[type="submit"], button[name="add"]').forEach((btn) => {
      if (btn instanceof HTMLButtonElement) btn.disabled = true;
    });
  });
}

/**
 * @param {string} hostProductId
 */
function enableFormSubmitControls(hostProductId) {
  const productFormComponent = findProductFormComponent(hostProductId);
  if (!productFormComponent) return;

  productFormComponent.querySelectorAll('add-to-cart-component').forEach((el) => {
    const component = /** @type {{ enable?: () => void }} */ (el);
    if (typeof component.enable === 'function') {
      component.enable();
      return;
    }
    el.querySelectorAll('button[type="submit"], button[name="add"]').forEach((btn) => {
      if (btn instanceof HTMLButtonElement) btn.disabled = false;
    });
  });
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLInputElement}
 */
function ensureBundleInput(form) {
  const existing = form.querySelector(`input[name="${BUNDLE_INPUT_NAME}"]`);
  if (existing instanceof HTMLInputElement) return existing;

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = BUNDLE_INPUT_NAME;
  input.value = '';
  form.appendChild(input);
  return input;
}

/**
 * Header customizes cart-icon with *two* cart-bubble trees (text + icon), both
 * using the same `ref="cartBubbleCount"`. Horizon Component refs bind only one,
 * so theme cart-icon updates often miss the visible bubble until full reload.
 * Always write absolute counts into every matching node.
 * @param {number} itemCount
 */
function syncHeaderCartBubbles(itemCount) {
  const count = Math.max(0, Number(itemCount) || 0);
  const label = count < 100 ? String(count) : '';

  document.querySelectorAll('cart-icon').forEach((icon) => {
    icon.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__text-count').forEach((el) => {
      el.textContent = label;
      el.classList.toggle('hidden', count === 0);
    });
    icon.querySelectorAll('[ref="cartBubble"]').forEach((el) => {
      el.classList.toggle('visually-hidden', count === 0);
    });
    icon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
  });

  sessionStorage.setItem(
    'cart-count',
    JSON.stringify({ value: String(count), timestamp: Date.now() })
  );
}

/**
 * Absolute bubble sync from `/cart.js` after any cart:update (add/remove).
 * Debounced so Host CartAddEvent + our composite CartUpdateEvent coalesce.
 */
function scheduleCartBubbleSyncFromServer() {
  window.clearTimeout(cartBubbleSyncTimer);
  cartBubbleSyncTimer = window.setTimeout(async () => {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      syncHeaderCartBubbles(cart.item_count);
    } catch {
      // ignore
    }
  }, 50);
}

/**
 * @param {string} hostProductId
 */
function clearInFlightSafetyTimer(hostProductId) {
  const timer = inFlightSafetyTimers.get(hostProductId);
  if (timer === undefined) return;
  clearTimeout(timer);
  inFlightSafetyTimers.delete(hostProductId);
}

/**
 * @param {string} hostProductId
 * @param {string} flightBundleId
 */
function scheduleInFlightSafetyRelease(hostProductId, flightBundleId) {
  clearInFlightSafetyTimer(hostProductId);
  const timer = setTimeout(() => {
    inFlightSafetyTimers.delete(hostProductId);
    const state = bundleStateByHost.get(hostProductId);
    if (!state?.inFlight || state.bundleId !== flightBundleId) return;
    state.inFlight = false;
    bundleStateByHost.set(hostProductId, state);
    enableFormSubmitControls(hostProductId);
  }, IN_FLIGHT_SAFETY_MS);
  inFlightSafetyTimers.set(hostProductId, timer);
}

/**
 * @param {PersonalizeCartSet[]} sets
 * @returns {PersonalizeCartSet[]}
 */
function snapshotSets(sets) {
  return sets.map((set) => ({
    setId: set.setId,
    charms: Array.isArray(set.charms) ? [...set.charms] : [],
  }));
}

/**
 * @param {PersonalizeCartSet[]} sets
 * @returns {boolean}
 */
function setsHaveCharms(sets) {
  return sets.some((set) => Array.isArray(set.charms) && set.charms.length > 0);
}

/**
 * @param {string} hostProductId
 * @param {PersonalizeCartSet[]} sets
 */
function syncHiddenBundleInput(hostProductId, sets) {
  const productFormComponent = findProductFormComponent(hostProductId);
  if (!productFormComponent) return;

  const form = findProductForm(productFormComponent);
  if (!form) return;

  const existing = form.querySelector(`input[name="${BUNDLE_INPUT_NAME}"]`);

  if (sets.length === 0) {
    existing?.remove();
    return;
  }

  if (!existing) {
    ensureBundleInput(form);
  }
}

document.addEventListener('personalize:sets-changed', (event) => {
  const customEvent = /** @type {CustomEvent<{hostProductId?: string, sets?: PersonalizeCartSet[]}>} */ (
    event
  );
  const hostProductId = String(customEvent.detail?.hostProductId ?? '');
  const sets = Array.isArray(customEvent.detail?.sets) ? customEvent.detail.sets : [];
  if (!hostProductId) return;

  pendingSetsByHost.set(hostProductId, sets);
  syncHiddenBundleInput(hostProductId, sets);
});

window.addEventListener(
  'submit',
  (event) => {
    const target = event.target;
    const productFormComponent =
      target instanceof Element ? target.closest('product-form-component') : null;
    const hostProductId = productFormComponent?.dataset.productId;
    if (!productFormComponent || !hostProductId) return;

    const form = findProductForm(productFormComponent);
    if (!form) return;

    const submitted = target instanceof Element ? target.closest('form') : null;
    if (submitted !== form) return;

    const state = bundleStateByHost.get(hostProductId) ?? {
      bundleId: null,
      inFlight: false,
    };

    if (state.inFlight) {
      // Second click: preventDefault alone is not enough — Horizon still submits
      // unless ATC is disabled. First click must NOT disable (instant ATC UX).
      event.preventDefault();
      disableFormSubmitControls(productFormComponent);
      return;
    }

    const sets = pendingSetsByHost.get(hostProductId) ?? [];
    if (setsHaveCharms(sets)) {
      const input = ensureBundleInput(form);
      state.bundleId = crypto.randomUUID();
      state.flightSets = snapshotSets(sets);
      input.value = state.bundleId;
      state.inFlight = true;
      bundleStateByHost.set(hostProductId, state);
      scheduleInFlightSafetyRelease(hostProductId, state.bundleId);
      // Do not disable ATC here — Host+composite stay responsive; inFlight gates duplicates.
      return;
    }

    form.querySelector(`input[name="${BUNDLE_INPUT_NAME}"]`)?.remove();
    state.flightSets = undefined;
    bundleStateByHost.set(hostProductId, state);
  },
  { capture: true }
);

/**
 * @param {string} hostProductId
 * @returns {HTMLElement | null}
 */
function findPersonalizeCharmsComponent(hostProductId) {
  if (!hostProductId) return null;
  const byHostAttr = document.querySelector(
    `personalize-charms-component[data-host-product-id="${CSS.escape(hostProductId)}"]`
  );
  if (byHostAttr) return byHostAttr;

  const productFormComponent = findProductFormComponent(hostProductId);
  return productFormComponent?.querySelector('personalize-charms-component') ?? null;
}

/**
 * @param {string} hostProductId
 */
function clearPendingSets(hostProductId) {
  pendingSetsByHost.set(hostProductId, []);
  syncHiddenBundleInput(hostProductId, []);

  const component = findPersonalizeCharmsComponent(hostProductId);
  if (
    component &&
    typeof /** @type {{ clearPendingSets?: () => void }} */ (component).clearPendingSets === 'function'
  ) {
    /** @type {{ clearPendingSets: () => void }} */ (component).clearPendingSets();
  }
}

/**
 * @returns {string}
 */
function collectCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.push(item.dataset.sectionId);
    }
  });
  return ids.join(',');
}

/**
 * Horizon success CartAddEvent puts variant id in `sourceId`, not `data.variantId`.
 * On didError, `sourceId` is the component id — fall back to form input.
 * @param {Event} event
 * @param {string} hostProductId
 * @returns {string}
 */
function resolveHostVariantId(event, hostProductId) {
  const sourceId = String(/** @type {{ detail?: { sourceId?: string } }} */ (event).detail?.sourceId ?? '');
  if (/^\d+$/.test(sourceId)) return sourceId;

  const productFormComponent = findProductFormComponent(hostProductId);
  const form = productFormComponent ? findProductForm(productFormComponent) : null;
  const input = form?.querySelector('input[name="id"]');
  if (input instanceof HTMLInputElement && input.value) return String(input.value);

  return '';
}

/**
 * Full-removal rollback of the just-added Host line (AD-2b / H8).
 * Match by `_charms_bundle_id` (+ variant when known). Always quantity 0.
 * Never matches charm lines (`_charms_set_id` / `_charms_position`).
 * @param {string} hostVariantId
 * @param {string} _hostProductId
 * @param {string} bundleId
 * @returns {Promise<unknown | undefined>} change.js `sections` HTML when present
 */
async function rollbackHostLine(hostVariantId, _hostProductId, bundleId) {
  if (!bundleId) return;

  const cartRes = await fetch('/cart.js');
  if (!cartRes.ok) {
    throw new Error(`Cart fetch failed (${cartRes.status})`);
  }
  const cart = await cartRes.json();
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const hostLine = items.find((item) => {
    const props = item?.properties ?? {};
    if (props._charms_bundle_id !== bundleId) return false;
    // Host carries bundle id only — charm lines also have set/position props.
    if (props._charms_set_id != null || props._charms_position != null) return false;
    if (!hostVariantId) return true;
    return String(item.variant_id) === String(hostVariantId);
  });
  if (!hostLine?.key) return;

  const sections = collectCartSectionIds();
  /** @type {{ id: string, quantity: number, sections?: string, sections_url?: string }} */
  const body = { id: hostLine.key, quantity: 0 };
  if (sections) {
    body.sections = sections;
    body.sections_url = window.location.pathname;
  }

  const changeRes = await fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  });
  const data = await changeRes.json();
  if (!changeRes.ok || data.status || data.errors) {
    throw new Error(data.message || data.description || 'Host rollback failed');
  }
  return data.sections;
}

/**
 * @param {PersonalizeCartSet[]} sets
 * @returns {PersonalizeCartCharm[]}
 */
function flattenFlightCharms(sets) {
  return sets.flatMap((set) => (Array.isArray(set.charms) ? set.charms : []));
}

/**
 * @param {PersonalizeCartCharm} charm
 * @returns {{ symbol: string, color: string }}
 */
function namedCharmOrGeneric(charm) {
  return {
    symbol: String(charm.symbol ?? '') || 'charm',
    color: String(charm.color ?? '') || 'selected',
  };
}

/**
 * @param {unknown} err
 * @param {PersonalizeCartSet[]} sets
 * @returns {{ symbol: string, color: string }}
 */
function resolveFailedCharm(err, sets) {
  const charms = flattenFlightCharms(sets);
  const cause =
    err && typeof err === 'object' && 'cause' in err
      ? /** @type {{ cause?: Record<string, unknown> }} */ (err).cause
      : null;

  /** @type {string[]} */
  const parts = [];
  if (cause?.description != null) parts.push(String(cause.description));
  if (cause?.message != null) parts.push(String(cause.message));
  if (cause?.errors != null) {
    const errors = cause.errors;
    if (typeof errors === 'string') parts.push(errors);
    else if (Array.isArray(errors)) parts.push(...errors.map(String));
    else if (typeof errors === 'object') parts.push(JSON.stringify(errors));
  }
  const haystack = parts.join(' ').toLowerCase();

  if (haystack) {
    // Prefer variant id before loose label substrings (single-letter symbols).
    const byVariant = charms.find((charm) => {
      const id = String(charm.variantId ?? '');
      return id && haystack.includes(id);
    });
    if (byVariant) return namedCharmOrGeneric(byVariant);

    const byBoth = charms.find((charm) => {
      const symbol = String(charm.symbol ?? '').toLowerCase();
      const color = String(charm.color ?? '').toLowerCase();
      if (!symbol || !color) return false;
      return haystack.includes(symbol) && haystack.includes(color);
    });
    if (byBoth) return namedCharmOrGeneric(byBoth);

    // Skip 1-char tokens — "A" matches "already" in sold-out copy.
    const byLabel = charms.find((charm) => {
      const symbol = String(charm.symbol ?? '').toLowerCase();
      const color = String(charm.color ?? '').toLowerCase();
      const symbolOk = symbol.length >= 2 && haystack.includes(symbol);
      const colorOk = color.length >= 2 && haystack.includes(color);
      return symbolOk || colorOk;
    });
    if (byLabel) return namedCharmOrGeneric(byLabel);
  }

  if (charms.length === 1) {
    return namedCharmOrGeneric(charms[0]);
  }

  // Multi-charm unknown — generic fill (never blame charms[0]).
  return { symbol: 'charm', color: 'selected' };
}

/**
 * @param {string} hostProductId
 * @param {string} message
 */
function renderProductFormError(hostProductId, message) {
  const productFormComponent = findProductFormComponent(hostProductId);
  if (!productFormComponent) return;

  const errorEl =
    productFormComponent.querySelector('[ref="addToCartTextError"]') ??
    productFormComponent.querySelector('.product-form-text__error');
  if (!(errorEl instanceof HTMLElement)) return;

  errorEl.classList.remove('hidden');
  errorEl.setAttribute('aria-live', 'assertive');

  const textNode = errorEl.childNodes[2];
  if (textNode) {
    textNode.textContent = message;
  } else {
    errorEl.appendChild(document.createTextNode(message));
  }

  const existingTimer = partialFailureErrorTimers.get(hostProductId);
  if (existingTimer !== undefined) clearTimeout(existingTimer);
  const timer = setTimeout(() => {
    partialFailureErrorTimers.delete(hostProductId);
    errorEl.classList.add('hidden');
    errorEl.removeAttribute('aria-live');
  }, PARTIAL_FAILURE_ERROR_DISPLAY_MS);
  partialFailureErrorTimers.set(hostProductId, timer);
}

/**
 * Liquid `| escape` may leave literal &#39; etc. in data-* when double-encoded.
 * @param {string} value
 * @returns {string}
 */
function decodeHtmlEntities(value) {
  if (!value || !value.includes('&')) return value;
  const el = document.createElement('textarea');
  el.innerHTML = value;
  return el.value;
}

/**
 * FR-22 named sold-out error. Always call after composite failure (even if rollback no-ops).
 * @param {unknown} err
 * @param {PersonalizeCartSet[]} sets
 * @param {string} hostProductId
 */
function showPartialFailureError(err, sets, hostProductId) {
  const { symbol, color } = resolveFailedCharm(err, sets);
  const component = findPersonalizeCharmsComponent(hostProductId);
  const rawTemplate =
    (component instanceof HTMLElement && component.dataset.i18nPartialFailure) ||
    "We couldn't add your full order — __SYMBOL__ (__COLOR__) just sold out. Remove or replace it and try again.";
  const template = decodeHtmlEntities(rawTemplate);
  const message = template.replaceAll('__SYMBOL__', symbol).replaceAll('__COLOR__', color);
  renderProductFormError(hostProductId, message);
}

/**
 * Best-effort cart UI refresh after rollback (drawer morph + bubbles).
 * @param {string} [sections]
 */
async function refreshCartUiAfterMutation(sections) {
  try {
    const cart = await fetch('/cart.js').then((r) => r.json());
    const itemCount = Number(cart.item_count) || 0;
    syncHeaderCartBubbles(itemCount);
    /** @type {{ source: string, itemCount: number, sections?: unknown }} */
    const detail = {
      source: 'personalize-cart',
      itemCount,
    };
    if (sections) detail.sections = sections;
    document.dispatchEvent(new CartUpdateEvent(cart, 'personalize-cart', detail));
  } catch {
    // ignore enrichment failure — FR-22 / unlock must still proceed
  }
}

/**
 * Unlock ATC for a flight without clearing pending sets (failure / didError paths).
 * @param {string} hostProductId
 * @param {string | null} flightBundleId
 */
function unlockFlight(hostProductId, flightBundleId) {
  const current = bundleStateByHost.get(hostProductId);
  if (!current) {
    enableFormSubmitControls(hostProductId);
    return;
  }
  if (flightBundleId && current.bundleId !== flightBundleId) return;

  current.inFlight = false;
  current.bundleId = null;
  current.flightSets = undefined;
  bundleStateByHost.set(hostProductId, current);
  clearInFlightSafetyTimer(hostProductId);

  // Drop failed flight UUID from the form; pending sets stay for retry.
  // Next guarded submit mints a fresh UUID into this input.
  const productFormComponent = findProductFormComponent(hostProductId);
  const form = productFormComponent ? findProductForm(productFormComponent) : null;
  const input = form?.querySelector(`input[name="${BUNDLE_INPUT_NAME}"]`);
  if (input instanceof HTMLInputElement) input.value = '';

  enableFormSubmitControls(hostProductId);
}

/**
 * Host line for the just-added personalized collar (AD-2 step 3 / AD-3).
 * Match variant + `_charms_bundle_id`; never a charm line or a bare same-variant.
 * @param {{ items?: Array<{ key?: string, variant_id?: string | number, properties?: Record<string, string | null | undefined> }> } | null | undefined} cart
 * @param {string} hostVariantId
 * @param {string} bundleId
 * @returns {{ key?: string, variant_id?: string | number, properties?: Record<string, string | null | undefined> } | null}
 */
function findHostLineByVariantAndBundle(cart, hostVariantId, bundleId) {
  if (!bundleId) return null;
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return (
    items.find((item) => {
      const props = item?.properties ?? {};
      if (props._charms_bundle_id !== bundleId) return false;
      if (props._charms_set_id != null || props._charms_position != null) return false;
      if (!hostVariantId) return true;
      return String(item.variant_id) === String(hostVariantId);
    }) ?? null
  );
}

/**
 * Composite `/cart/add.js` items: nest under Host, set/position only (AD-3).
 * @param {PersonalizeCartSet[]} sets
 * @param {string} parentLineKey
 * @returns {Array<{ id: number | string, quantity: number, parent_line_key: string, properties: { _charms_set_id: string, _charms_position: string } }>}
 */
function buildCharmCartItems(sets, parentLineKey) {
  return sets.flatMap((set) =>
    set.charms.map((charm, index) => ({
      id: Number(charm.variantId) || charm.variantId,
      quantity: 1,
      parent_line_key: parentLineKey,
      properties: {
        _charms_set_id: set.setId,
        _charms_position: String(index + 1),
      },
    }))
  );
}

/**
 * @param {PersonalizeCartSet[]} sets
 * @param {string} bundleId
 * @param {string} hostVariantId
 */
async function addCharmsToCart(sets, bundleId, hostVariantId) {
  const cartRes = await fetch('/cart.js');
  if (!cartRes.ok) {
    throw new Error(`Cart fetch failed (${cartRes.status})`);
  }
  const cartBefore = await cartRes.json();
  const hostLine = findHostLineByVariantAndBundle(cartBefore, hostVariantId, bundleId);
  if (!hostLine?.key) {
    throw new Error('Charms add failed: Host line not found');
  }

  const items = buildCharmCartItems(sets, hostLine.key);

  if (items.length === 0) {
    throw new Error('Charms add failed: empty items payload');
  }

  const sections = collectCartSectionIds();
  /** @type {{ items: typeof items, sections?: string, sections_url?: string }} */
  const body = { items };
  if (sections) {
    body.sections = sections;
    body.sections_url = window.location.pathname;
  }

  // No AbortController — keepalive must survive navigation / slow networks (AD-2b).
  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  });
  const data = await response.json();

  if (!response.ok || data.status || data.errors) {
    const err = new Error(data.message || 'Charms add failed');
    /** @type {Error & { cause?: unknown }} */ (err).cause = data;
    throw err;
  }

  // Charms are committed — `/cart.js` enrichment is best-effort only.
  // Never dispatch itemCount: 0 on enrichment failure — cart-icon treats missing
  // non-product-form updates as absolute `itemCount ?? 0` and zeros the bubble.
  /** @type {Record<string, unknown> | null} */
  let cart = null;
  /** @type {number | undefined} */
  let itemCount;
  try {
    cart = await fetch('/cart.js').then((r) => r.json());
    itemCount = Number(cart?.item_count) || 0;
  } catch {
    // ignore — do not treat composite as failed after a successful add
  }

  // Never use instanceof CartAddEvent/CartUpdateEvent — duplicate @theme/events
  // module graphs make instanceof fail across product-form vs this file.
  /** @type {{ source: string, itemCount?: number, sections?: unknown }} */
  const detail = {
    source: 'personalize-cart',
    sections: data.sections,
  };
  if (itemCount !== undefined) detail.itemCount = itemCount;

  document.dispatchEvent(new CartUpdateEvent(cart ?? data, 'personalize-cart', detail));
  if (itemCount !== undefined) syncHeaderCartBubbles(itemCount);
  else scheduleCartBubbleSyncFromServer();
  return { data, cart };
}

// Keep header bubbles honest for bare ATC, composite, and cart-page removals.
document.addEventListener(ThemeEvents.cartUpdate, scheduleCartBubbleSyncFromServer);

/**
 * AD-5b / FR-29 — cart-level "Remove Set" (set-scoped, never Host / other sets).
 * Distinct from AD-5 Host cascade — do not call cascadeOrphanCharmsForRemovedHost.
 */
document.addEventListener('click', async (event) => {
  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  const button = target?.closest?.('[data-charms-remove-set]');
  if (!(button instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();

  if (removeSetInFlight || cascadeInFlight) return;

  const setId = button.dataset.charmsRemoveSet;
  if (typeof setId !== 'string' || setId === '') return;

  removeSetInFlight = true;
  button.setAttribute('disabled', 'true');
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    const cart = await res.json();
    const setLineKeys = collectSetLineKeys(cart, setId);
    if (setLineKeys.length === 0) return;
    await zeroCartLinesByKeys(setLineKeys);
  } catch (err) {
    console.error('[personalize-cart] Remove Set failed', err);
  } finally {
    removeSetInFlight = false;
    // Button may be gone after remorph — only clear disabled if still in DOM.
    if (button.isConnected) button.removeAttribute('disabled');
  }
});

/**
 * AD-5 Host cascade fallback — dedicated listener (source-gated).
 * Native nested cascade is primary. This only POSTs update.js if children remain.
 * Never shares the composite-add branch; ignores personalize-cart / in-flight re-entry.
 */
document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  const data = event.detail?.data;
  if (!data) return;

  // Our own remorph / composite completion — refresh snapshot only, never cascade.
  if (data.source === 'personalize-cart' || cascadeInFlight) {
    const resource = event.detail?.resource;
    if (resource && Array.isArray(resource.items)) {
      lastKnownCart = resource;
    } else {
      await refreshLastKnownCartFromServer();
    }
    return;
  }

  // Non-cart-items mutations (e.g. product-form Host add): keep snapshot warm for FR-26.
  if (data.source !== 'cart-items-component') {
    await refreshLastKnownCartFromServer();
    return;
  }

  let cart;
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    cart = await res.json();
  } catch {
    return;
  }

  const removedHost = findHostLineThatWentToZero(lastKnownCart, cart);
  if (!removedHost) {
    lastKnownCart = cart;
    return;
  }

  await cascadeOrphanCharmsForRemovedHost(removedHost, cart);
});

document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  // Source-only gate (intentional): duplicate @theme/events graphs break instanceof.
  // Native Host add uses product-form-component; our completion uses personalize-cart.
  const data = event.detail?.data;
  if (!data || data.source !== 'product-form-component') return;

  const hostProductId = String(data.productId ?? '');
  if (!hostProductId) return;

  const state = bundleStateByHost.get(hostProductId);
  if (!state?.inFlight) return;

  const didError = Boolean(data.didError);
  clearInFlightSafetyTimer(hostProductId);

  const flightBundleId = state.bundleId;
  const sets = Array.isArray(state.flightSets)
    ? state.flightSets
    : [...(pendingSetsByHost.get(hostProductId) ?? [])];

  // Native max-qty (or other) error: Horizon may still have added Host + `_charms_bundle_id`.
  // Clean the orphan Host; do not show FR-22 sold-out copy; leave pending for retry.
  if (didError) {
    if (flightBundleId) {
      const hostVariantId = resolveHostVariantId(event, hostProductId);
      /** @type {unknown} */
      let sections;
      try {
        sections = await rollbackHostLine(hostVariantId, hostProductId, flightBundleId);
      } catch (rollbackErr) {
        console.error('[personalize-cart] didError orphan Host rollback failed', rollbackErr);
      }
      await refreshCartUiAfterMutation(sections);
    }
    unlockFlight(hostProductId, flightBundleId);
    return;
  }

  if (!flightBundleId || !setsHaveCharms(sets)) {
    unlockFlight(hostProductId, flightBundleId);
    clearPendingSets(hostProductId);
    return;
  }

  // Keep inFlight through composite (duplicate gate); do not long-disable ATC.
  scheduleInFlightSafetyRelease(hostProductId, flightBundleId);
  const hostVariantId = resolveHostVariantId(event, hostProductId);

  try {
    await addCharmsToCart(sets, flightBundleId, hostVariantId);
    // Skip clear if 15s safety unlock (or a newer flight) already superseded this id.
    const stillCurrent = bundleStateByHost.get(hostProductId);
    if (stillCurrent?.bundleId === flightBundleId) {
      clearPendingSets(hostProductId);
    }
  } catch (err) {
    console.error('[personalize-cart] composite charms add failed', err);
    /** @type {unknown} */
    let sections;
    try {
      sections = await rollbackHostLine(hostVariantId, hostProductId, flightBundleId);
    } catch (rollbackErr) {
      console.error('[personalize-cart] rollback failed', rollbackErr);
    }
    // Always surface FR-22 even if rollback no-ops or throws (AD-2b).
    showPartialFailureError(err, sets, hostProductId);
    await refreshCartUiAfterMutation(sections);
  } finally {
    unlockFlight(hostProductId, flightBundleId);
  }
});

// Prime Host-cascade snapshot (best-effort; null-safe if fetch fails).
primeLastKnownCart();
